import { Done } from '@mui/icons-material';
import { CircularProgress, InputAdornment } from '@mui/material';
import { FC, useEffect, useState } from 'react';

import { AsyncSearch } from 'components';
import { useCustomerAPI } from 'hooks/use-customer-api';
import { ActiveUnit, Customer } from 'types';

import useFleetFileUnits from '../use-fleet-file-units';
import WarningAdornment from '../warning-adornment';
import { fleetAccountConflict, fleetCandidatesHint } from './fleet-account-check';
import { useInboundOrderCheck } from './use-inbound-order-check';

type Props = {
  unit: Partial<ActiveUnit>;
  required?: boolean;
  setCustomer: (customer?: Customer | null) => void;
  ['data-cy']?: string;
};

const InboundAccountSearch: FC<Props> = ({ unit, required, setCustomer, ...props }) => {
  const { searchCustomers } = useCustomerAPI();
  const { getFleetFileUnit } = useFleetFileUnits();
  const number = unit.unitNumber?.trim().toUpperCase() ?? '';
  const customerId = unit.customer?.id;
  const exempt = !!unit.flatbed || !!unit.ownerChassis;
  const check = useInboundOrderCheck(number, customerId, !exempt);
  const [fleet, setFleet] = useState<{ key: string; hint: string | null; warning: boolean } | null>(null);
  const fleetKey = JSON.stringify([number, customerId, unit.equipmentType, exempt]);

  // Fleet advice has its own lifetime and can never change order validity or the selected account.
  useEffect(() => {
    let current = true;
    if (!number || exempt) return;
    getFleetFileUnit(number, unit.equipmentType ?? undefined)
      .then(outcome => {
        if (!current) return;
        let hint: string | null = null;
        let warning = false;
        if (outcome.kind === 'found') {
          const conflict = customerId ? fleetAccountConflict(outcome.record, number, customerId) : null;
          hint = conflict ?? fleetCandidatesHint(outcome.record, customerId);
          warning = !!conflict;
        } else if (outcome.kind === 'denied') {
          hint = `Not permitted to check the fleet file for ${number}`;
          warning = true;
        } else if (['unavailable', 'timeout', 'invalidData', 'invalidRequest'].includes(outcome.kind)) {
          warning = true;
        }
        setFleet({ key: fleetKey, hint, warning });
      })
      .catch(() => {
        if (current) setFleet({ key: fleetKey, hint: null, warning: true });
      });
    return () => {
      current = false;
    };
  }, [number, customerId, unit.equipmentType, exempt, fleetKey, getFleetFileUnit]);

  const advice = fleet?.key === fleetKey ? fleet : null;
  const invalid = check.status !== 'allowed';
  const validationMessage = invalid ? check.message ?? 'Gate-in is not allowed for this account.' : '';
  const adornment =
    check.status === 'pending' ? (
      <InputAdornment position="end">
        <CircularProgress size={24} />
      </InputAdornment>
    ) : invalid || advice?.warning ? (
      <WarningAdornment />
    ) : customerId ? (
      <InputAdornment position="end">
        <Done />
      </InputAdornment>
    ) : null;

  return (
    <div className="account-async-search">
      <AsyncSearch<Customer>
        label="ACCOUNT"
        value={unit.customer}
        required={required}
        formatOption={({ name }) => name}
        formatValue={customer => customer?.name ?? ''}
        handleSelect={customer => setCustomer(customer)}
        search={searchCustomers}
        validationMessage={validationMessage}
        adornment={adornment}
        data-cy={props['data-cy']}
      />
      {check.message && (
        <small role={invalid ? 'alert' : 'status'} data-cy="inbound-order-message">
          {check.message}
        </small>
      )}
      {check.status === 'unavailable' && (
        <button type="button" className="btn-outline" onClick={check.retry} data-cy="retry-inbound-order-check">
          Retry order check
        </button>
      )}
      {!!advice?.hint && (
        <small className="fleet-candidates-hint" data-cy="fleet-candidates-hint">
          {advice.hint}
        </small>
      )}
    </div>
  );
};

export default InboundAccountSearch;
